import napi from '@/utils/request';

const CONSENT_KEY = 'cookie_consent';
const GA_SCRIPT_SELECTOR = 'script[data-cookie-consent-ga="true"], script[src="https://www.google-analytics.com/analytics.js"]';
const FB_PIXEL_SCRIPT_SELECTOR = 'script[data-cookie-consent-fb-pixel="true"], script[src="https://connect.facebook.net/en_US/fbevents.js"]';
const GA_TRACKING_ID = 'UA-48483439-1';

function getConsentFlag(key) {
  const consent = getConsent();
  return !!(consent && consent[key]);
}

function hasQueuedGa() {
  return typeof window.ga === 'function' && Array.isArray(window.ga.q) && !!window.ga.l;
}

function hasReadyGa() {
  return typeof window.ga === 'function' && (typeof window.ga.getAll === 'function' || !!window.ga.loaded);
}

function hasUsableGa() {
  return hasQueuedGa() || hasReadyGa() || !!window.__gaTrackerCreated;
}

function hasReadyFbq() {
  return typeof window.fbq === 'function' && (!!window.fbq.callMethod || !!window.fbq.queue || !!window.fbq.loaded);
}

function flushGoogleConversionQueue() {
  if (!window.__googleTrackConversionQueue) {
    return;
  }

  if (!isMarketingLoaded()) {
    window.__googleTrackConversionQueue = [];
    return;
  }

  if (!window.google_trackConversion) {
    return;
  }

  while (window.__googleTrackConversionQueue.length) {
    window.google_trackConversion(window.__googleTrackConversionQueue.shift());
  }
}

/**
 * Get current cookie consent preferences from localStorage
 * @returns {Object|null} consent preferences or null if not set
 */
export function getConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    // ignore parse errors
  }
  return null;
}

/**
 * Check if user has already given consent (any preference saved)
 * @returns {boolean}
 */
export function hasConsent() {
  return getConsent() !== null;
}

/**
 * Save cookie consent preferences to localStorage
 * @param {Object} preferences - { analytics: boolean, marketing: boolean }
 */
export function saveConsent(preferences) {
  const data = {
    essential: true,
    analytics: !!preferences.analytics,
    marketing: !!preferences.marketing,
    timestamp: new Date().toISOString()
  };
  window.localStorage.setItem(CONSENT_KEY, JSON.stringify(data));

  // Backend'e consent bilgisini gönder
  napi.post('/consents', {
    essential: data.essential,
    analytics: data.analytics,
    marketing: data.marketing
  }).catch(() => {
    // Consent backend'e gönderilemese bile localStorage'a kaydedildi
  });

  window[`ga-disable-${GA_TRACKING_ID}`] = !data.analytics;

  if (!data.marketing) {
    window.__googleTrackConversionQueue = [];
  }
}

/**
 * Load Google Analytics script dynamically
 */
export function loadAnalyticsScripts() {
  if (window.__gaLoaded && hasUsableGa()) return;
  window.__gaLoaded = true;
  window[`ga-disable-${GA_TRACKING_ID}`] = !getConsentFlag('analytics');

  if (!hasReadyGa()) {
    window.GoogleAnalyticsObject = 'ga';
    window.ga = function () {
      (window.ga.q = window.ga.q || []).push(arguments);
    };
    window.ga.q = window.ga.q || [];
    window.ga.l = window.ga.l || (1 * new Date());
  }

  if (!document.querySelector(GA_SCRIPT_SELECTOR)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.google-analytics.com/analytics.js';
    script.setAttribute('data-cookie-consent-ga', 'true');
    document.head.appendChild(script);
  }

  if (!window.__gaTrackerCreated) {
    window.ga('create', GA_TRACKING_ID, 'auto');
    window.__gaTrackerCreated = true;
  }
}

/**
 * Load marketing/advertising scripts dynamically (Google Ads + Facebook)
 */
export function loadMarketingScripts() {
  if (window.__marketingLoaded && hasReadyFbq()) return;
  window.__marketingLoaded = true;

  // Google Ad Services
  if (!document.querySelector('script[src="https://www.googleadservices.com/pagead/conversion_async.js"]')) {
    var gads = document.createElement('script');
    gads.type = 'text/javascript';
    gads.src = 'https://www.googleadservices.com/pagead/conversion_async.js';
    gads.charset = 'utf-8';
    gads.onload = flushGoogleConversionQueue;
    document.head.appendChild(gads);
  } else {
    flushGoogleConversionQueue();
  }

  // Facebook SDK
  window.fbAsyncInit = function () {
    window.FB.init({
      appId: '965371270280769',
      cookie: true,
      xfbml: true,
      version: 'v2.11'
    });
  };

  (function (d, s, id) {
    var js, fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) { return; }
    js = d.createElement(s);
    js.id = id;
    js.src = '//connect.facebook.net/en_US/sdk.js';
    fjs.parentNode.insertBefore(js, fjs);
  }(document, 'script', 'facebook-jssdk'));

  // Facebook Pixel
  if (!hasReadyFbq()) {
    var fbq = function () {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };

    if (!window._fbq) {
      window._fbq = fbq;
    }

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
  }

  if (!document.querySelector(FB_PIXEL_SCRIPT_SELECTOR)) {
    var pixel = document.createElement('script');
    pixel.async = true;
    pixel.src = 'https://connect.facebook.net/en_US/fbevents.js';
    pixel.setAttribute('data-cookie-consent-fb-pixel', 'true');
    document.head.appendChild(pixel);
  }

  window.fbq('init', '1444157569165122');
  window.fbq('track', 'PageView');
}

export function trackGoogleConversion(config) {
  if (!isMarketingLoaded()) {
    return;
  }

  if (window.google_trackConversion) {
    window.google_trackConversion(config);
    return;
  }

  window.__googleTrackConversionQueue = window.__googleTrackConversionQueue || [];
  window.__googleTrackConversionQueue.push(config);
}

/**
 * Initialize scripts based on saved consent preferences
 * Called on app mount / page load
 */
export function initConsentBasedScripts() {
  const consent = getConsent();
  if (!consent) return;

  if (consent.analytics) {
    loadAnalyticsScripts();
  }

  if (consent.marketing) {
    loadMarketingScripts();
  }
}

/**
 * Check if analytics scripts are loaded and available
 * @returns {boolean}
 */
export function isAnalyticsLoaded() {
  return getConsentFlag('analytics') && !!window.__gaLoaded && hasUsableGa();
}

/**
 * Check if marketing scripts are loaded and available
 * @returns {boolean}
 */
export function isMarketingLoaded() {
  return getConsentFlag('marketing') && !!window.__marketingLoaded && hasReadyFbq();
}
